import { useState, useCallback, useEffect, useRef } from 'react';
import { generateImage } from "@/api/imageApi"
import { 
  generateVideo, 
  uploadVideo, 
  cancelVideoGeneration as cancelVideoAPI
} from "@/api/videoApi"
import { auth } from "@/lib/picasso-firebase"
import { onAuthStateChanged, User } from 'firebase/auth';
import { DEFAULT_MODEL, AIModel, IMAGE_MODELS, supportsMultipleImages } from '../../constants/models';
import { validateVideoGeneration } from '@/utils/dashboard/validation/videoValidation';
import { UploadingVideo, useOptimisticUploadLegacy } from '@/utils/optimistic/optimisticUpload';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';
import { generateIdempotencyKey } from '@/utils/dashboard/idempotency';

// Type alias for tab values
type TabType = 'Videos' | 'Images' | 'Transcripts';

interface VideoJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message?: string;
  video_url?: string;
  thumbnail_url?: string;
  metadata?: {
    duration: number;
    resolution: string;
    format: string;
    [key: string]: any;
  };
  error_message?: string;
}

interface UseVideoGenerationReturn {
  prompt: string;
  selectedModel: AIModel;
  isVideoGenerating: boolean;
  isGenerating: boolean;
  error: string | null;
  progress: number;
  progressMessage: string;
  generatedVideo: VideoJobStatus | null;
  selectedImage: File | null;
  imagePreview: string | null;
  generateAudio: boolean;
  aspectRatio: string;
  imageAspectRatio: string;
  imageResolution: string;
  isUploading: boolean;
  uploadError: string | null;
  uploadSuccess: string | null;
  canCancel: boolean;
  isCancelling: boolean;
  wasCancelled: boolean;
  queuedVideos: QueuedVideo[];
  queuedImages: QueuedVideo[];
  uploadingVideos: UploadingVideo[];
  uploadedImagesCount: number;
  setPrompt: (prompt: string) => void;
  setAspectRatio: (ratio: string) => void;
  setImageAspectRatio: (ratio: string) => void;
  setImageResolution: (resolution: string) => void;
  handleModelSelect: (model: string) => void;
  handleGenerateWithTile: (onAddQueuedVideo?: (queuedVideo: QueuedVideo) => void, bypassForbiddenWords?: boolean, currentTab?: TabType) => Promise<void>;
  handleCancel: () => Promise<void>;
  cancelQueuedVideo: (queuedId: string) => Promise<void>;
  cancelQueuedImage: (queuedId: string) => Promise<void>;
  clearError: () => void;
  handleImageUpload: (file: File, currentTab: TabType) => void;
  removeImage: (currentTab: TabType) => void;
  removeImageAtIndex: (index: number, currentTab: TabType) => void;
  videoUploadedImages: File[];
  videoImagePreviews: string[];
  setVideoUploadedImages: React.Dispatch<React.SetStateAction<File[]>>;
  setVideoImagePreviews: React.Dispatch<React.SetStateAction<string[]>>;
  imageGenUploadedImages: File[];
  imageGenPreviews: string[];
  setImageGenUploadedImages: React.Dispatch<React.SetStateAction<File[]>>;
  setImageGenPreviews: React.Dispatch<React.SetStateAction<string[]>>;
  toggleAudio: () => void;
  handleVideoUpload: (video: File, prompt: string, model: string) => Promise<void>;
  clearUploadError: () => void;
  clearUploadSuccess: () => void;
  handleVideoGenerated: (videoInfo: any) => void;
  setIsVideoGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  getMaxImageCount: (currentTab: TabType) => number;
  originalVideoId: string | null;
  setOriginalVideoId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useVideoGeneration = (_onVideoGenerated?: () => void): UseVideoGenerationReturn => {
  const [prompt, setPrompt] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<AIModel>(() => {
    const savedModel = localStorage.getItem('selectedModel');
    if (savedModel === 'Whisper') return 'Whisper';
    if (savedModel === 'Gemini 3 Pro') return 'Gemini 3 Pro';
    if (savedModel === 'Gemini 2.5') return 'Gemini 2.5';
    return DEFAULT_MODEL;
  });
  const [isVideoGenerating, setIsVideoGenerating] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [generatedVideo, ] = useState<VideoJobStatus | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // Separate states for video vs image generation
  const [videoUploadedImages, setVideoUploadedImages] = useState<File[]>([]);
  const [videoImagePreviews, setVideoImagePreviews] = useState<string[]>([]);
  const [imageGenUploadedImages, setImageGenUploadedImages] = useState<File[]>([]);
  const [imageGenPreviews, setImageGenPreviews] = useState<string[]>([]);
  const [generateAudio, setGenerateAudio] = useState<boolean>(true);
  const [aspectRatio, setAspectRatio] = useState<string>('16:9');
  const [imageAspectRatio, setImageAspectRatio] = useState<string>('1:1');
  const [imageResolution, setImageResolution] = useState<string>('1k');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authInitialized, setAuthInitialized] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  
  // Original video ID for extended video generation (when creating from last frame)
  const [originalVideoId, setOriginalVideoId] = useState<string | null>(null);
  
  // Persistent queued state with localStorage backup
  const [queuedVideosState, setQueuedVideosState] = useState<QueuedVideo[]>([]);
  const [queuedImagesState, setQueuedImagesState] = useState<QueuedVideo[]>([]);
  
  // Helper functions for localStorage persistence
  const getStorageKey = useCallback((type: 'videos' | 'images' | 'uploading') => {
    return `queuedTiles_${type}_${currentUser?.uid || 'anonymous'}`;
  }, [currentUser]);

  const loadFromStorage = useCallback((type: 'videos' | 'images'): QueuedVideo[] => {
    try {
      const stored = localStorage.getItem(getStorageKey(type));
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load tiles from localStorage:', error);
      return [];
    }
  }, [getStorageKey]);

  const loadUploadingFromStorage = useCallback((): UploadingVideo[] => {
    try {
      const stored = localStorage.getItem(getStorageKey('uploading'));
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load uploading tiles from localStorage:', error);
      return [];
    }
  }, [getStorageKey]);

  // Track last saved state to prevent unnecessary saves
  const lastSavedVideos = useRef<string>('');
  const lastSavedImages = useRef<string>('');
  const lastSavedUploading = useRef<string>('');
  const saveTimers = useRef<{ videos?: NodeJS.Timeout; images?: NodeJS.Timeout; uploading?: NodeJS.Timeout }>({});

  const saveToStorage = useCallback((type: 'videos' | 'images', data: QueuedVideo[], immediate = false) => {
    try {
      const key = getStorageKey(type);
      const serialized = JSON.stringify(data);

      // Check if data has actually changed
      const lastSaved = type === 'videos' ? lastSavedVideos : lastSavedImages;
      if (lastSaved.current === serialized) {
        return; // No change, skip save
      }

      // Update immediately to prevent duplicate checks
      lastSaved.current = serialized;

      // Clear any pending save for this type
      if (saveTimers.current[type]) {
        clearTimeout(saveTimers.current[type]);
      }

      if (immediate) {
        // Save immediately for critical updates (status transitions, removals)
        localStorage.setItem(key, serialized);
      } else {
        // Debounce saves by 100ms to batch rapid updates (progress updates, etc.)
        saveTimers.current[type] = setTimeout(() => {
          localStorage.setItem(key, serialized);
        }, 100);
      }
    } catch (error) {
      console.error('Failed to save tiles to localStorage:', error);
    }
  }, [getStorageKey]);

  const saveUploadingToStorage = useCallback((data: UploadingVideo[], immediate: boolean = false) => {
    try {
      const key = getStorageKey('uploading');
      const serialized = JSON.stringify(data);

      // Check if data has actually changed
      if (lastSavedUploading.current === serialized) {
        return; // No change, skip save
      }

      // Update immediately to prevent duplicate checks
      lastSavedUploading.current = serialized;

      // Clear any pending save
      if (saveTimers.current.uploading) {
        clearTimeout(saveTimers.current.uploading);
      }

      if (immediate) {
        // Save immediately without debounce (for removals)
        localStorage.setItem(key, serialized);
      } else {
        // Debounce saves by 100ms to batch rapid updates (for additions/updates)
        saveTimers.current.uploading = setTimeout(() => {
          localStorage.setItem(key, serialized);
        }, 100);
      }
    } catch (error) {
      console.error('Failed to save uploading tiles to localStorage:', error);
    }
  }, [getStorageKey]);

  // Helper to detect critical changes that need immediate save
  const hasCriticalChanges = useCallback((prevTiles: QueuedVideo[], newTiles: QueuedVideo[]): boolean => {
    // Status changes
    const hasStatusChange = prevTiles.some(oldTile => {
      const newTile = newTiles.find(t => t.id === oldTile.id);
      return newTile && newTile.status !== oldTile.status;
    });

    // TaskId assignments
    const hasTaskIdAssignment = prevTiles.some(oldTile => {
      const newTile = newTiles.find(t => t.id === oldTile.id);
      return newTile && !oldTile.taskId && newTile.taskId;
    });

    // Items removed
    const itemsRemoved = newTiles.length < prevTiles.length;

    return hasStatusChange || hasTaskIdAssignment || itemsRemoved;
  }, []);
  
  // Wrapper functions that update both state and localStorage
  const setQueuedVideos = useCallback((updater: QueuedVideo[] | ((prev: QueuedVideo[]) => QueuedVideo[]), immediate = false) => {
    setQueuedVideosState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      const shouldSaveImmediately = immediate || hasCriticalChanges(prev, newState);
      saveToStorage('videos', newState, shouldSaveImmediately);
      return newState;
    });
  }, [hasCriticalChanges, saveToStorage]);

  const setQueuedImages = useCallback((updater: QueuedVideo[] | ((prev: QueuedVideo[]) => QueuedVideo[]), immediate = false) => {
    setQueuedImagesState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      const shouldSaveImmediately = immediate || hasCriticalChanges(prev, newState);
      saveToStorage('images', newState, shouldSaveImmediately);
      return newState;
    });
  }, [hasCriticalChanges, saveToStorage]);

  // Expose the current state values
  const queuedVideos = queuedVideosState;
  const queuedImages = queuedImagesState;
  
  // Track if we've already loaded from storage for this user
  const hasLoadedRef = useRef<string | null>(null);

  // Helper function to clean up truly orphaned tiles without taskId
  const cleanOrphanedTiles = useCallback((tiles: QueuedVideo[]) => {
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);
    const fifteenMinutesAgo = now - (15 * 60 * 1000); // Max VEO timeout is 10 min

    return tiles.filter(tile => {
      const tileCreatedTime = tile.createdAt ? new Date(tile.createdAt).getTime() : 0;

      // Always remove tiles older than 15 minutes — no VEO job runs longer than 10 min,
      // so anything older is stale (WebSocket event was missed while tab was closed)
      if (tileCreatedTime < fifteenMinutesAgo) {
        return false;
      }

      // Keep if has taskId (backend has picked it up)
      if (tile.taskId) return true;

      // Keep if waiting for backend or currently processing
      if (tile.status === 'queued' || tile.status === 'processing') {
        return true;
      }

      // Keep if created recently (protect new tiles without taskId yet)
      if (tileCreatedTime > fiveMinutesAgo) {
        return true;
      }

      // Remove tiles without taskId that are in final states AND old (orphaned)
      return false;
    });
  }, []);

  // Helper function to determine if an uploading tile should be kept
  const shouldKeepUploadingTile = useCallback((tile: UploadingVideo, now: number) => {
    const thirtySecondsAgo = now - (30 * 1000); // 30 seconds
    const twoMinutesAgo = now - (2 * 60 * 1000); // 2 minutes

    // Always remove failed tiles
    if (tile.status === 'failed') return false;

    // Always remove completed tiles (shouldn't be in uploading list)
    if (tile.status === 'completed') return false;

    // Get upload start time (use uploadStartTime if available, fallback to createdAt)
    const uploadTime = tile.uploadStartTime || new Date(tile.createdAt).getTime();
    if (Number.isNaN(uploadTime)) return false; // Invalid timestamp

    // CRITICAL: When page refreshes, the upload HTTP request is cancelled by the browser
    // So any "uploading" tile older than 30 seconds is definitely stale
    // (Real uploads take seconds, not minutes - network request doesn't survive refresh)

    if (tile.status === 'uploading') {
      // Keep very recent uploads (< 30 seconds) - they might still be in progress
      return uploadTime > thirtySecondsAgo;
    }

    // For 'processing' status - keep for up to 2 minutes
    // Processing happens on backend after upload completes
    if (tile.status === 'processing') {
      return uploadTime > twoMinutesAgo;
    }

    return false; // Remove unknown status
  }, []);

  // Helper function to clean up old/stale uploading tiles with smart detection
  const cleanStaleUploadingTiles = useCallback((tiles: UploadingVideo[]) => {
    const now = Date.now();
    return tiles.filter(tile => shouldKeepUploadingTile(tile, now));
  }, [shouldKeepUploadingTile]);

  // Load initial state from localStorage ONCE per user
  useEffect(() => {
    // WAIT for auth to complete before loading
    if (!authInitialized || !currentUser) {
      return;
    }

    const userId = currentUser.uid;

    // Only load if we haven't loaded for this user yet
    if (hasLoadedRef.current === userId) {
      return;
    }

    // Track when we start loading
    loadStartTimeRef.current = Date.now();

    // Defer localStorage loading to avoid blocking initial render
    // This significantly improves page load performance
    const loadData = () => {
      const storedVideos = loadFromStorage('videos');
      const storedImages = loadFromStorage('images');

      const cleanedVideos = cleanOrphanedTiles(storedVideos);
      const cleanedImages = cleanOrphanedTiles(storedImages);

      // Save cleaned tiles back to localStorage
      if (cleanedVideos.length !== storedVideos.length) {
        saveToStorage('videos', cleanedVideos);
      }
      if (cleanedImages.length !== storedImages.length) {
        saveToStorage('images', cleanedImages);
      }

      if (cleanedVideos.length > 0) {
        setQueuedVideosState(cleanedVideos);
      }
      if (cleanedImages.length > 0) {
        setQueuedImagesState(cleanedImages);
      }

      // Load uploading videos from storage
      const storedUploading = loadUploadingFromStorage();

      const cleanedUploading = cleanStaleUploadingTiles(storedUploading);

      // Save cleaned tiles back to localStorage if changed
      if (cleanedUploading.length !== storedUploading.length) {
        saveUploadingToStorage(cleanedUploading, true); // Immediate save
      }

      if (cleanedUploading.length > 0) {
        setUploadingVideosState(cleanedUploading);
      }

      // Mark as loaded for this user
      hasLoadedRef.current = userId;
    };

    // Use requestIdleCallback for better performance (falls back to setTimeout)
    if (typeof requestIdleCallback === 'undefined') {
      setTimeout(loadData, 0);
    } else {
      requestIdleCallback(loadData, { timeout: 500 });
    }
  }, [authInitialized, currentUser, cleanOrphanedTiles, cleanStaleUploadingTiles, loadFromStorage, loadUploadingFromStorage, saveToStorage, saveUploadingToStorage]);

  const [uploadingVideosState, setUploadingVideosState] = useState<UploadingVideo[]>([]);

  // Wrapper function that updates both state and localStorage
  const setUploadingVideos = useCallback((updater: UploadingVideo[] | ((prev: UploadingVideo[]) => UploadingVideo[])) => {
    setUploadingVideosState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;

      // If array is getting smaller (items removed), save immediately without debounce
      // This prevents tiles from reappearing on reload
      const isRemoval = newState.length < prev.length;
      saveUploadingToStorage(newState, isRemoval);

      return newState;
    });
  }, [saveUploadingToStorage]);

  // Expose the current state value
  const uploadingVideos = uploadingVideosState;
  const isUploading = uploadingVideos.some(video => video.status === 'uploading');

  // Optimistic upload functions
  const {
    optimisticAddUploadingVideo,
    optimisticUpdateUploadingVideo,
    optimisticRemoveUploadingVideo
  } = useOptimisticUploadLegacy(setUploadingVideos, () => {});
  // Cancellation state
  const [canCancel, setCanCancel] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  // Ref to track status polling interval
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track if generation was manually cancelled to prevent popup reappearance
  const wasCancelledRef = useRef<boolean>(false);
  // Ref to track current task ID to prevent race conditions
  const currentTaskIdRef = useRef<string | null>(null);
  // Ref to track current generation attempt to ignore old responses
  const generationCounterRef = useRef<number>(0);
  // Ref to track progress polling intervals for queued videos
  const progressIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const API_BASE_URL = import.meta.env.VITE_BASE_URL; 

  // Helper function to generate secure random IDs
  const generateSecureId = useCallback((prefix: string): string => {
    // Use crypto.randomUUID() if available, fallback to timestamp + counter
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
    // Fallback for environments without crypto.randomUUID
    const timestamp = Date.now().toString(36);
    const counter = Math.floor(performance.now() * 1000).toString(36);
    return `${prefix}_${timestamp}_${counter}`;
  }, []);

 // Helper function to create authenticated headers
  const createAuthHeaders = useCallback(async (includeContentType = true): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error("Failed to get authentication token:", error);
        throw new Error('Failed to get authentication token');
      }
    } else {
      throw new Error('User not authenticated');
    }

    return headers;
  }, [currentUser]);

  // Hybrid approach: WebSocket for real-time + Polling as safety net
  // - WebSocket: Real-time updates when connected (primary)
  // - Polling: Safety check on reload and for stuck tiles (fallback)

  // Track whether this is the initial reload poll or regular polling
  const isInitialPollRef = useRef(true);

  const checkTaskStatus = useCallback(async (taskId: string, isReloadPoll: boolean = false) => {
    if (!taskId) return;

    try {
      const headers = await createAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/picasso/video/task-status/${taskId}`, {
        headers,
      });

      const data = await response.json();

      if (data.status === 'SUCCESS') {
        // During initial reload poll: DO NOT remove tiles
        // Let DashboardPage auto-cleanup handle it after videos/images are fetched
        // This prevents tiles from disappearing before the video/image appears in gallery
        if (isInitialPollRef.current) {
          // During initial poll, just mark as completed but keep tile visible
          setQueuedVideos(prev => prev.map(v =>
            v.taskId === taskId ? { ...v, status: 'completed' as const } : v
          ));
          setQueuedImages(prev => prev.map(i =>
            i.taskId === taskId ? { ...i, status: 'completed' as const } : i
          ));
        } else if (data.video_id) {
          // Task completed! Remove tile (only during regular polling, not reload)
          setQueuedVideos(prev => prev.filter(v => v.taskId !== taskId));
          setQueuedImages(prev => prev.filter(i => i.taskId !== taskId));
        }
      } else if (data.status === 'FAILURE' || data.status === 'ERROR') {
        // Task failed, mark tile as failed
        setQueuedVideos(prev => prev.map(v =>
          v.taskId === taskId ? { ...v, status: 'failed' as const } : v
        ));
        setQueuedImages(prev => prev.map(i =>
          i.taskId === taskId ? { ...i, status: 'failed' as const } : i
        ));
      } else if (data.status === 'STARTED' || data.status === 'PROGRESS') {
        // Only update tile to processing if it's NOT the initial reload poll
        // During reload: preserve queued status, let WebSocket handle the transition
        // During regular polling: allow status update (WebSocket fallback)
        if (!isReloadPoll && !isInitialPollRef.current) {
          setQueuedVideos(prev => prev.map(v =>
            v.taskId === taskId && v.status === 'queued'
              ? { 
                  ...v, 
                  status: 'processing' as const, 
                  startedAt: v.startedAt || new Date().toISOString() // Preserve existing startedAt if already set
                }
              : v
          ));
          setQueuedImages(prev => prev.map(i =>
            i.taskId === taskId && i.status === 'queued'
              ? { 
                  ...i, 
                  status: 'processing' as const, 
                  startedAt: i.startedAt || new Date().toISOString() // Preserve existing startedAt if already set
                }
              : i
          ));
        }
      }
      // PENDING status = task still queued, no action needed
    } catch (error) {
      console.error('Failed to check task status:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, createAuthHeaders]);

  // 1. Immediate polling on reload: Check all restored tiles ONCE (after loading from storage)
  const hasPolledAfterLoad = useRef(false);
  const loadStartTimeRef = useRef(Date.now());
  const [storageLoadComplete, setStorageLoadComplete] = useState(false);

  // Track when storage load completes
  useEffect(() => {
    if (!authInitialized || !currentUser) return;

    // Wait a tick for localStorage load to complete
    const timer = setTimeout(() => {
      setStorageLoadComplete(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [authInitialized, currentUser]);

  // Poll tiles once after storage loads
  useEffect(() => {
    if (!authInitialized || !currentUser || !storageLoadComplete || hasPolledAfterLoad.current) {
      return;
    }

    const allTiles = [...queuedVideosState, ...queuedImagesState];

    if (allTiles.length > 0) {
      hasPolledAfterLoad.current = true;
      isInitialPollRef.current = true; // Mark this as initial reload poll

      for (const tile of allTiles) {
        if (tile.taskId && tile.status !== 'completed' && tile.status !== 'failed') {
          checkTaskStatus(tile.taskId, true); // Pass isReloadPoll = true
        }
      }

      // After initial poll completes, mark it as done
      setTimeout(() => {
        isInitialPollRef.current = false;
      }, 3000); // Keep initial poll mode for 3 seconds to align with cleanupReady (2s)
    } else {
      // Only mark as polled if we've waited long enough for tiles to load
      // Check if we're past the initial load window (500ms + buffer)
      const hasWaitedEnough = Date.now() - loadStartTimeRef.current > 1000;
      if (hasWaitedEnough) {
        hasPolledAfterLoad.current = true;
        isInitialPollRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, currentUser, storageLoadComplete, queuedVideosState, queuedImagesState, checkTaskStatus]);

  // 2. Regular polling: Check all active tiles every 5s
  useEffect(() => {
    const interval = setInterval(() => {
      const allTiles = [...queuedVideosState, ...queuedImagesState];

      for (const tile of allTiles) {
        if (!tile.taskId || tile.status === 'completed' || tile.status === 'failed') {
          continue;
        }

        // Poll queued tiles to detect when Celery picks them up
        // Pass isReloadPoll = false to allow status updates during regular polling
        if (tile.status === 'queued') {
          checkTaskStatus(tile.taskId, false);
        }

        // Poll processing tiles that exceeded expected time (stuck)
        if (tile.status === 'processing' && tile.startedAt) {
          const elapsed = Date.now() - new Date(tile.startedAt).getTime();
          // Hunyuan and Mochi: 20min, VEO: 15min
          const maxTime = (tile.model === 'Hunyuan' || tile.model === 'Mochi') ? 20 * 60 * 1000 : 15 * 60 * 1000;

          if (elapsed > maxTime) {
            checkTaskStatus(tile.taskId, false);
          }
        }
      }
    }, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [queuedVideosState, queuedImagesState, checkTaskStatus]);

  // Progress is calculated client-side based on elapsed time (10min VEO, 20min Hunyuan/Mochi)

  // Progress polling function for image generation
  // Helper function to clean up intervals and timeouts
  const cleanupTimers = useCallback((): void => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
    // Clean up all progress polling timeouts
    for (const [, timeout] of progressIntervalsRef.current) {
      clearTimeout(timeout);
    }
    progressIntervalsRef.current.clear();
  }, []);

  // Helper function to reset generation state
  const resetGenerationState = useCallback((clearMessage = true): void => {
    setIsGenerating(false);
    setProgress(0);
    if (clearMessage) {
      setProgressMessage('');
    }
    setCanCancel(false);
    setIsCancelling(false);
    currentTaskIdRef.current = null;
  }, []);

  // Listen to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, []);

  // WebSocket integration for real-time video generation updates
  const { subscribe, unsubscribe: wsUnsubscribe } = useWebSocket();

  // Helper function to handle video generation started
  const handleGenerationStarted = useCallback((dataObj: any) => {
    const { tile_id, task_id, model } = dataObj;
    if (!tile_id) return;

    // Update both videos and images (both use same WebSocket event)
    setQueuedVideos(prev => {
      return prev.map(v =>
        v.id === tile_id
          ? {
              ...v,
              status: 'processing' as const,
              taskId: task_id,
              startedAt: v.startedAt || new Date().toISOString(), // Preserve existing startedAt if already set
              model: model || v.model
            }
          : v
      );
    }, true); // Force immediate save for status transition
    
    setQueuedImages(prev => {
      return prev.map(i =>
        i.id === tile_id
          ? {
              ...i,
              status: 'processing' as const,
              taskId: task_id,
              startedAt: i.startedAt || new Date().toISOString(), // Preserve existing startedAt if already set
              model: model || i.model
            }
          : i
      );
    }, true); // Force immediate save for status transition
  }, [setQueuedVideos, setQueuedImages]);

  // NOTE: handleGenerationCompleted removed - polling now handles completion detection
  // When task completes, checkTaskStatus (polling) removes the tile
  // This prevents old WebSocket events from removing tiles on page reload

  // Helper function to handle video generation cancelled
  const handleGenerationCancelled = useCallback((dataObj: any) => {
    const { tile_id } = dataObj;
    if (!tile_id) {
      return;
    }

    // During initial reload window: Don't remove tiles from old cancel events
    // Only process cancellations that happen during active session
    if (isInitialPollRef.current) {
      return;
    }

    // Remove from both videos and images
    setQueuedVideos(prev => prev.filter(v => v.id !== tile_id));
    setQueuedImages(prev => prev.filter(i => i.id !== tile_id));

    // Reset generation state to re-enable the generate button
    resetGenerationState(false);
  }, [setQueuedVideos, setQueuedImages, resetGenerationState]);

  // Helper function to handle video generation failed
  const handleGenerationFailed = useCallback((dataObj: any) => {
    const { tile_id } = dataObj;
    if (!tile_id) {
      return;
    }

    // Update both videos and images
    setQueuedVideos(prev => {
      const updated = prev.map(v =>
        v.id === tile_id
          ? {
              ...v,
              status: 'failed' as const,
              progress: 0,
              errorMessage: dataObj.error_message || 'Generation failed.',
            }
          : v
      );
      return updated;
    });

    setQueuedImages(prev => {
      const updated = prev.map(i =>
        i.id === tile_id
          ? {
              ...i,
              status: 'failed' as const,
              progress: 0,
              errorMessage: dataObj.error_message || 'Generation failed.',
            }
          : i
      );
      return updated;
    });

    // Reset generation state to re-enable the generate button
    resetGenerationState(false);
  }, [setQueuedVideos, setQueuedImages, resetGenerationState]);

  // Helper function to handle video generation completion (from WebSocket)
  const handleGenerationCompletedFromWS = useCallback((dataObj: any) => {
    const { tile_id } = dataObj;
    if (!tile_id) return;

    // During initial reload window: Mark as completed instead of removing
    // Let DashboardPage auto-cleanup handle removal after videos/images are fetched
    if (isInitialPollRef.current) {
      setQueuedVideos(prev => prev.map(v =>
        v.id === tile_id ? { ...v, status: 'completed' as const } : v
      ));
      setQueuedImages(prev => prev.map(i =>
        i.id === tile_id ? { ...i, status: 'completed' as const } : i
      ));
    } else {
      // After initial window: Safe to remove tiles immediately
      setQueuedVideos(prev => prev.filter(v => v.id !== tile_id));
      setQueuedImages(prev => prev.filter(i => i.id !== tile_id));
    }
  }, [setQueuedVideos, setQueuedImages]);

  useEffect(() => {
    const handleWebSocketMessage = (data: any) => {
      let parsedData;
      try {
        parsedData = typeof data === 'string' ? JSON.parse(data) : data;
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
        return;
      }

      const messageType = parsedData.type || parsedData.event;
      const dataObj = parsedData.data || parsedData;

      if (messageType === 'video_generation_started') {
        handleGenerationStarted(dataObj);
      } else if (messageType === 'video_generation_cancelled') {
        handleGenerationCancelled(dataObj);
      } else if (messageType === 'video_generation_failed') {
        handleGenerationFailed(dataObj);
      } else if (messageType === 'video_generated') {
        // Handle completion by removing tile with matching tile_id
        // This handles completion when user is on a different page
        handleGenerationCompletedFromWS(dataObj);
      }
    };

    subscribe('video-generation-updates', handleWebSocketMessage);

    return () => {
      wsUnsubscribe('video-generation-updates');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, wsUnsubscribe, handleGenerationStarted, handleGenerationCancelled, handleGenerationFailed, handleGenerationCompletedFromWS]);

  // Helper function to calculate progress for a video tile
  const calculateVideoProgress = useCallback((v: QueuedVideo): QueuedVideo => {
    if (v.status !== 'processing' || !v.startedAt) return v;
    
    const elapsedMs = Date.now() - new Date(v.startedAt).getTime();
    const elapsedMin = elapsedMs / (1000 * 60);
    
    // Hunyuan and Mochi use 20 min, VEO uses 10 min
    const isHunyuanOrMochi = v.model === 'Hunyuan' || v.model === 'Mochi';
    const maxDuration = isHunyuanOrMochi ? 20 : 10;
    
    const calculatedProgress = Math.min(95, (elapsedMin / maxDuration) * 100);
    
    return {
      ...v,
      progress: Math.round(calculatedProgress)
    };
  }, []);

  // Helper function to calculate progress for an image tile (1 minute duration)
  const calculateImageProgress = useCallback((img: QueuedVideo): QueuedVideo => {
    if (img.status !== 'processing' || !img.startedAt) return img;
    
    const elapsedMs = Date.now() - new Date(img.startedAt).getTime();
    const elapsedMin = elapsedMs / (1000 * 60);
    
    // Images use 1 min duration
    const maxDuration = 1;
    
    const calculatedProgress = Math.min(95, (elapsedMin / maxDuration) * 100);
    
    return {
      ...img,
      progress: Math.round(calculatedProgress)
    };
  }, []);

  // Helper function to calculate progress for uploading video (3 minute duration)
  const calculateUploadingVideoProgress = useCallback((video: UploadingVideo): UploadingVideo => {
    if (!video.uploadStartTime) return video;
    
    const elapsedMs = Date.now() - video.uploadStartTime;
    const elapsedMin = elapsedMs / (1000 * 60);
    
    // Videos use 3 min duration
    const maxDuration = 3;
    
    const calculatedProgress = Math.min(95, (elapsedMin / maxDuration) * 100);
    
    return {
      ...video,
      progress: Math.round(calculatedProgress)
    };
  }, []);

  // Helper functions to update progress state (reduces nesting depth)
  const updateVideosProgress = useCallback(() => {
    setQueuedVideos(prev => prev.map(v => calculateVideoProgress(v)));
  }, [calculateVideoProgress, setQueuedVideos]);

  const updateImagesProgress = useCallback(() => {
    setQueuedImages(prev => prev.map(i => calculateImageProgress(i)));
  }, [calculateImageProgress, setQueuedImages]);

  const updateUploadingProgress = useCallback(() => {
    setUploadingVideos(prev => prev.map(u => calculateUploadingVideoProgress(u)));
  }, [calculateUploadingVideoProgress, setUploadingVideos]);

  // Client-side progress simulation for processing tiles
  useEffect(() => {
    // Run immediately on mount to start progress calculation
    updateVideosProgress();
    updateImagesProgress();
    updateUploadingProgress();
    
    const interval = setInterval(() => {
      updateVideosProgress();
      updateImagesProgress();
      updateUploadingProgress();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [updateVideosProgress, updateImagesProgress, updateUploadingProgress]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      cleanupTimers();
      // Reset cancellation flag on unmount
      wasCancelledRef.current = false;
      // Increment generation counter to invalidate any pending responses
      generationCounterRef.current += 1;
    };
  }, [cleanupTimers]);

  // Function to handle video generation/upload completion (called by useUserVideos)
  const handleVideoGenerated = useCallback((videoInfo: any) => {

    // Remove any queued videos that match this generated video
    setQueuedVideos(prev => {
      const updatedQueue = prev.filter(queuedVideo => {
        // Remove if prompts match (indicating this queued video was completed)
        const promptsMatch = queuedVideo.prompt === videoInfo.prompt;

        if (promptsMatch) {
          // Stop any polling for this video
          const interval = progressIntervalsRef.current.get(queuedVideo.id);
          if (interval) {
            clearInterval(interval);
            progressIntervalsRef.current.delete(queuedVideo.id);
          }
        }

        return !promptsMatch;
      });

      return updatedQueue;
    });

    // Also remove any queued images that match this generated image
    setQueuedImages(prev => {
      const updatedQueue = prev.filter(queuedImage => {
        // Remove if prompts match (indicating this queued image was completed)
        const promptsMatch = queuedImage.prompt === videoInfo.prompt;

        if (promptsMatch) {
          // Stop any polling for this image
          const interval = progressIntervalsRef.current.get(queuedImage.id);
          if (interval) {
            clearInterval(interval);
            progressIntervalsRef.current.delete(queuedImage.id);
          }
        }

        return !promptsMatch;
      });

      return updatedQueue;
    });

    // Also remove any uploading videos that match this uploaded video
    setUploadingVideos(prev => {
      const updatedUploading = prev.filter(uploadingVideo => {
        // Remove if prompts match (indicating this uploading video was completed)
        const promptsMatch = uploadingVideo.prompt === videoInfo.prompt;
        return !promptsMatch;
      });

      return updatedUploading;
    });
  }, [setQueuedVideos, setQueuedImages, setUploadingVideos]);

  // Helper to get max image count based on selected model
  const getMaxImageCount = useCallback((currentTab: TabType): number => {
    // For Videos tab, check if model supports multiple images
    if (currentTab === 'Videos') {
      return supportsMultipleImages(selectedModel) ? 3 : 1;
    }
    // Images tab always allows 3 images
    return 3;
  }, [selectedModel]);

  const handleModelSelect = useCallback((model: string): void => {
    const newModel = model as AIModel;
    const oldModel = selectedModel;
    setSelectedModel(newModel);
    setError(null);
    
    // Only trim video images if BOTH old and new models are video models
    // (don't trim when switching between Videos/Images/Transcripts tabs)
    const oldIsVideoModel = !IMAGE_MODELS.includes(oldModel) && oldModel !== 'Whisper';
    const newIsVideoModel = !IMAGE_MODELS.includes(newModel) && newModel !== 'Whisper';
    
    // If switching from a multi-image video model to a single-image video model, keep only the first image
    const oldSupportsMultiple = supportsMultipleImages(oldModel);
    const newSupportsMultiple = supportsMultipleImages(newModel);
    
    if (
      oldIsVideoModel &&
      newIsVideoModel &&
      oldSupportsMultiple &&
      !newSupportsMultiple &&
      (videoImagePreviews.length > 1 || videoUploadedImages.length > 1)
    ) {
      setVideoUploadedImages(prev => prev.slice(0, 1));
      setVideoImagePreviews(prev => prev.slice(0, 1));
    }
  }, [selectedModel, videoImagePreviews.length, videoUploadedImages.length, setVideoUploadedImages, setVideoImagePreviews]);

  const handleImageUpload = useCallback((file: File, currentTab: TabType): void => {
    if (file?.type.startsWith('image/')) {
      const isVideosTab = currentTab === 'Videos';
      const isImagesTab = currentTab === 'Images';
      const maxImages = getMaxImageCount(currentTab);
      
      // Check upload limits based on current tab and model
      if (isVideosTab && videoImagePreviews.length >= maxImages) {
        const modelName = supportsMultipleImages(selectedModel)
          ? `${selectedModel} allows maximum ${maxImages} image uploads` 
          : `${selectedModel} allows only 1 image upload`;
        setError(modelName);
        return;
      }
      
      if (isImagesTab && imageGenPreviews.length >= maxImages) {
        setError('Images tab allows maximum 3 images');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        
        if (isVideosTab) {
          setVideoUploadedImages(prev => {
            if (prev.length >= maxImages) return prev;
            return [...prev, file];
          });
          setVideoImagePreviews(prev => {
            if (prev.length >= maxImages) return prev;
            return [...prev, preview];
          });
        } else if (isImagesTab) {
          setImageGenUploadedImages(prev => {
            if (prev.length >= maxImages) return prev;
            return [...prev, file];
          });
          setImageGenPreviews(prev => {
            if (prev.length >= maxImages) return prev;
            return [...prev, preview];
          });
        }
      };
      reader.readAsDataURL(file);
      setError(null);
    } else {
      setError('Please select a valid image file (JPG, PNG, etc.)');
    }
  }, [videoImagePreviews, imageGenPreviews, selectedModel, getMaxImageCount]);

  const removeImage = useCallback((currentTab: TabType): void => {
    if (currentTab === 'Videos') {
      setVideoUploadedImages([]);
      setVideoImagePreviews([]);
    } else if (currentTab === 'Images') {
      setImageGenUploadedImages(prev => prev.filter((_, index) => index !== prev.length - 1));
      setImageGenPreviews(prev => prev.filter((_, index) => index !== prev.length - 1));
    }
  }, []);

  const removeImageAtIndex = useCallback((index: number, currentTab: TabType): void => {
    if (currentTab === 'Videos') {
      // Remove specific image from video images arrays
      setVideoUploadedImages(prev => prev.filter((_, i) => i !== index));
      setVideoImagePreviews(prev => prev.filter((_, i) => i !== index));
    } else if (currentTab === 'Images') {
      // Remove specific image from image gen arrays
      setImageGenUploadedImages(prev => prev.filter((_, i) => i !== index));
      setImageGenPreviews(prev => prev.filter((_, i) => i !== index));
    }
  }, []);

  const toggleAudio = useCallback((): void => {
    setGenerateAudio(prev => {
      const newValue = !prev;
      return newValue;
    });
  }, []);

  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Return the full data URL (e.g., "data:image/jpeg;base64,...")
        // The backend expects this format to extract MIME type and base64 data
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCancel = useCallback(async (): Promise<void> => {
    if (!canCancel || isCancelling) {
      return;
    }

    setIsCancelling(true);
    setError(null);

    try {
      const result = await cancelVideoAPI(); // Note: we'd need to track job ID

      if (result.success) {
        // Mark that this generation was manually cancelled
        wasCancelledRef.current = true;

        // Immediately close the popup and reset all states
        // Don't wait for backend cleanup - handle it silently in background
        cleanupTimers();

        resetGenerationState();

        // Backend cleanup will happen silently - user doesn't need to see it
      } else {
        throw new Error(result.message || 'Failed to cancel video generation');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to cancel video generation');
      setIsCancelling(false);
    }
  }, [canCancel, isCancelling, cancelVideoAPI]); 
 const clearError = useCallback((): void => {
    setError(null);
    wasCancelledRef.current = false;
    currentTaskIdRef.current = null;
    // Don't reset generation counter here as it might interfere with ongoing requests
  }, []);

  const clearUploadError = useCallback((): void => {
    setUploadError(null);
  }, []);

  const clearUploadSuccess = useCallback((): void => {
    setUploadSuccess(null);
  }, []);

  const handleVideoUpload = useCallback(async (video: File, prompt: string, model: string): Promise<void> => {
    // Clear previous states
    setUploadError(null);
    setUploadSuccess(null);

    // Basic validation
    if (!video || !prompt || !model) {
      setUploadError('Please fill in all required fields');
      return;
    }

    // Validate file type
    if (!video.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      return;
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (video.size > maxSize) {
      setUploadError('Video file size exceeds 100MB limit');
      return;
    }

    // Check if authentication is initialized
    if (!authInitialized) {
      setUploadError('Authentication is still loading, please wait...');
      return;
    }

    // Check if user is logged in
    if (!currentUser) {
      setUploadError('Please log in to upload videos');
      return;
    }

    setUploadError(null);

    const promptText = prompt.trim();

    // Create uploading tile immediately for UI feedback
    const uploadingVideo = optimisticAddUploadingVideo(promptText, model, video);

    try {
      // Call upload API with tile_id (use uploading tile's ID for tracking)
      const result = await uploadVideo(video, promptText, model, uploadingVideo.id);

      if (result.success) {
        // Update uploading tile to processing if needed
        if (result.job_id) {
          optimisticUpdateUploadingVideo(uploadingVideo.id, {
            status: 'processing'
          });
        }

        // Set success message
        setUploadSuccess(result.message || 'Video upload started!');

        // WebSocket will handle:
        // - video_uploaded: Remove uploading tile and add video to gallery
      } else {
        // Upload failed - remove uploading tile
        optimisticRemoveUploadingVideo(uploadingVideo.id);
        throw new Error(result.message || 'Video upload failed');
      }

    } catch (err) {
      // Remove uploading tile on error
      optimisticRemoveUploadingVideo(uploadingVideo.id);

      const errorMessage = err instanceof Error ? err.message : 'Failed to upload video';
      setUploadError(errorMessage);
      throw err; // Re-throw for component to handle
    }
  }, [authInitialized, currentUser, uploadVideo, optimisticAddUploadingVideo, optimisticUpdateUploadingVideo, optimisticRemoveUploadingVideo, setUploadError, setUploadSuccess]);

  // Helper to validate auth and user state
  const validateAuthState = useCallback((): boolean => {
    if (!authInitialized) {
      setError('Authentication is still loading, please wait...');
      return false;
    }
    if (!currentUser) {
      setError('Please log in to generate videos');
      return false;
    }
    return true;
  }, [authInitialized, currentUser]);

  // Helper to convert GCS URL to gs:// URI format
  const convertToGcsUri = useCallback((imageUrl: string): string => {
    if (imageUrl.startsWith('https://storage.googleapis.com/')) {
      return imageUrl.replace('https://storage.googleapis.com/', 'gs://');
    }
    return imageUrl;
  }, []);

  // Helper to process images in order, maintaining sync between previews and uploaded files
  const processImagesInOrder = useCallback(async (
    imagePreviews: string[],
    uploadedFiles: (File | null)[],
    maxImages: number
  ): Promise<string[] | null> => {
    const results: string[] = [];
    try {
      for (let i = 0; i < Math.min(imagePreviews.length, maxImages); i++) {
        const preview = imagePreviews[i];
        const file = uploadedFiles[i];
        
        // If there's a file at this index, convert it to base64
        if (file instanceof File) {
          const base64Image = await convertImageToBase64(file);
          results.push(base64Image);
        }
        // Data URL (e.g. last frame extracted from video) — pass as-is
        else if (typeof preview === 'string' && preview.startsWith('data:')) {
          results.push(preview);
        }
        // Otherwise, it's a gallery URL - convert to GCS URI
        else if (typeof preview === 'string' && (preview.startsWith('http://') || preview.startsWith('https://'))) {
          results.push(convertToGcsUri(preview));
        }
      }
      return results;
    } catch (imageError) {
      console.error('Error processing images:', imageError);
      setError('Failed to process the selected images');
      return null;
    }
  }, [convertToGcsUri]);

  // Helper to prepare video generation request
  const prepareVideoRequest = useCallback(async (
    queuedVideoId: string,
    promptText: string
  ): Promise<any> => {
    const idempotencyKey = generateIdempotencyKey('veo');
    const request: any = {
      prompt: promptText,
      model: selectedModel,
      tile_id: queuedVideoId,
      idempotency_key: idempotencyKey,
      duration: 8,
      aspect_ratio: aspectRatio,
      quality: 'standard',
      generate_audio: generateAudio,
      ...(originalVideoId && { original_video_id: originalVideoId })
    };

    const MAX_IMAGES = 3;
    const allImageUrls = await processImagesInOrder(videoImagePreviews, videoUploadedImages, MAX_IMAGES);
    
    if (allImageUrls === null) return null;
    
    if (allImageUrls.length > 0) {
      request.image_urls = allImageUrls.slice(0, MAX_IMAGES);
    }

    return request;
  }, [selectedModel, aspectRatio, generateAudio, videoImagePreviews, videoUploadedImages, processImagesInOrder]);

  // Helper to create optimistic queued video (before API call)
  const createOptimisticQueuedVideo = useCallback((
    queuedVideoId: string,
    promptText: string,
    images: { previews: string[], files: (File | null)[] },
    onAddQueuedVideo?: (queuedVideo: QueuedVideo) => void
  ) => {
    // Use first image for the tile preview (prefer File over URL for better UX)
    let firstImage: File | string | undefined;
    if (images.files[0] instanceof File) {
      firstImage = images.files[0];
    } else if (images.previews[0]?.startsWith('http')) {
      firstImage = images.previews[0];
    }
    
    const queuedVideo: QueuedVideo = {
      id: queuedVideoId,
      prompt: promptText,
      model: selectedModel,
      status: 'queued',
      createdAt: new Date().toISOString(),
      image: firstImage,
      taskId: undefined // Will be set after API responds
    };

    setQueuedVideos(prev => [queuedVideo, ...prev]);

    if (onAddQueuedVideo) {
      onAddQueuedVideo(queuedVideo);
    }

    return queuedVideo;
  }, [selectedModel, setQueuedVideos]);

  // Helper to create optimistic queued image (before API call)
  const createOptimisticQueuedImage = useCallback((
    queuedImageId: string,
    promptText: string,
    currentImages: (File | string)[]
  ) => {
    const queuedImage: QueuedVideo = {
      id: queuedImageId,
      prompt: promptText,
      model: selectedModel,
      status: 'queued',
      createdAt: new Date().toISOString(),
      image: (typeof currentImages[0] === 'string' ? undefined : currentImages[0]) || undefined, // Use first File if available
      taskId: undefined // Will be set after API responds
    };

    setQueuedImages(prev => [queuedImage, ...prev]);

    return queuedImage;
  }, [selectedModel, setQueuedImages]);

  // Helper to update queued video with task ID after successful API response
  const updateQueuedVideoWithTaskId = useCallback((
    queuedVideoId: string,
    taskId: string
  ) => {
    setQueuedVideos(prev => prev.map(v =>
      v.id === queuedVideoId
        ? { ...v, taskId }
        : v
    ));
  }, [setQueuedVideos]);

  // Helper to remove queued video on API failure
  const removeQueuedVideo = useCallback((queuedVideoId: string) => {
    setQueuedVideos(prev => prev.filter(v => v.id !== queuedVideoId));
  }, [setQueuedVideos]);

  // Helper to handle generation errors
  const handleGenerationError = useCallback((result: any) => {
    const isAlreadyInProgress = result.message?.toLowerCase().includes('already have a video generation in progress');

    if (isAlreadyInProgress) {
      setError('You already have 1 video in progress. Please wait for it to complete.');
    } else {
    setError(result.message || 'Failed to start video generation');
    }
  }, []);

  // Type alias for model family to satisfy SonarQube
  type ModelFamily = 'Hunyuan' | 'Mochi' | 'VEO';

  // Helper to check if model family has processing video
  const checkModelFamilyInProgress = useCallback((modelFamily: ModelFamily): boolean => {
    return queuedVideos.some(v => {
      const isProcessingOrQueued = v.status === 'processing' || v.status === 'queued';
      
      let isSameModelFamily: boolean;
      if (modelFamily === 'Hunyuan') {
        isSameModelFamily = v.model === 'Hunyuan';
      } else if (modelFamily === 'Mochi') {
        isSameModelFamily = v.model === 'Mochi';
      } else {
        // VEO models - allow parallel generations, return false
        return false;
      }
      
      return isProcessingOrQueued && isSameModelFamily;
    });
  }, [queuedVideos]);

  // Helper to handle model family in progress error
  const handleModelFamilyInProgressError = useCallback((modelFamily: ModelFamily): void => {
    const errorMessage = `You already have 1 ${modelFamily} video in progress. Please wait for it to complete.`;
    // Force error to re-trigger every time using requestAnimationFrame
    requestAnimationFrame(() => {
      setError(errorMessage);
    });
  }, []);

  // Helper function to convert a single image to preview (extracted to reduce nesting)
  const convertImageToPreview = useCallback((img: File): Promise<string> => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.readAsDataURL(img);
    });
  }, []);

  // Helper to clear form after tile creation
  const clearGenerationForm = useCallback((): void => {
    setPrompt('');
    setSelectedImage(null);
    setImagePreview(null);
    setVideoUploadedImages([]);
    setVideoImagePreviews([]);
    setImageGenUploadedImages([]);
    setImageGenPreviews([]);
  }, []);

  // Helper to restore form on error
  const restoreGenerationForm = useCallback((promptText: string, images: { previews: string[], files: (File | null)[] }, currentImages?: (File | string)[]): void => {
    setPrompt(promptText);
    
    // Restore video images from the arrays
    if (images.previews.length > 0) {
      setVideoImagePreviews(images.previews);
      // Filter out nulls to match File[] type
      setVideoUploadedImages(images.files.filter((f): f is File => f instanceof File));
    }

    // For image generation with multiple images
    if (currentImages && currentImages.length > 0) {
      const fileImages = currentImages.filter((img): img is File => typeof img !== 'string');
      const urlImages = currentImages.filter((img): img is string => typeof img === 'string');
      
      setImageGenUploadedImages(fileImages);
      setImageGenPreviews(urlImages);
      
      // Restore file image previews
      if (fileImages.length > 0) {
        Promise.all(fileImages.map(img => convertImageToPreview(img))).then(previews => {
          setImageGenPreviews(prev => [...prev, ...previews]);
        });
      }
    }
  }, [convertImageToPreview]);

  // Helper to handle video generation API call
  const executeVideoGeneration = useCallback(async (
    queuedVideoId: string,
    promptText: string,
    images: { previews: string[], files: (File | null)[] }
  ): Promise<void> => {
    const request = await prepareVideoRequest(queuedVideoId, promptText);
    setIsVideoGenerating(true);
    if (!request) {
      // Failed to prepare request - remove the optimistic tile and restore prompt
      removeQueuedVideo(queuedVideoId);
      restoreGenerationForm(promptText, images);
      return;
    }

    const result = await generateVideo(request);

    if (result.success) {
      // API succeeded - update tile with task_id
      updateQueuedVideoWithTaskId(queuedVideoId, result.job_id);
    } else {
      // API failed - remove the optimistic tile, restore prompt, and show error
      removeQueuedVideo(queuedVideoId);
      restoreGenerationForm(promptText, images);
      handleGenerationError(result);
    }
  }, [prepareVideoRequest, removeQueuedVideo, restoreGenerationForm, updateQueuedVideoWithTaskId, handleGenerationError]);

  // Handle image generation for Gemini 2.5 model
  const handleImageGeneration = useCallback(async (): Promise<void> => {

    // Basic validation for image generation
    if (!prompt.trim()) {
      setError('Please enter a prompt for image generation');
      return;
    }

    // Check if authentication is initialized
    if (!authInitialized) {
      setError('Authentication is still loading, please wait...');
      return;
    }

    // Check if user is logged in
    if (!currentUser) {
      setError('Please log in to generate images');
      return;
    }

    // Clear any previous errors
    setError(null);

    // Save prompt and images before any async operations
    const promptText = prompt.trim();
    // Combine uploaded files and gallery URLs
    const uploadedFiles = imageGenUploadedImages;
    const galleryUrls = imageGenPreviews;
    const currentImages = [...uploadedFiles, ...galleryUrls];

    // OPTIMISTIC UI: Create tile IMMEDIATELY (before API call)
    const queuedImageId = generateSecureId('queued_img');
    createOptimisticQueuedImage(queuedImageId, promptText, currentImages);

    // Clear form IMMEDIATELY after creating tile
    clearGenerationForm();

    try {
      // Update status to processing with 0% progress
      setQueuedImages(prev => prev.map(img =>
        img.id === queuedImageId
          ? { ...img, status: 'processing' as const, progress: 0, startedAt: new Date().toISOString() }
          : img
      ));

      // Prepare the image generation request
      const request: any = {
        prompt: promptText,
        model: selectedModel,
        aspect_ratio: imageAspectRatio,
        quality: imageResolution,
        style: 'photographic',
        negative_prompt: ''
      };

      // Add input images if provided (for image editing/style transfer)
      // Always pass as an array, even for single images
      if (currentImages.length > 0) {
        try {
          const base64Images = await Promise.all(
            currentImages.map(async (image) => {
              // If it's a string (URL from gallery), convert to gs:// format
              if (typeof image === 'string') {
                // Convert HTTPS GCS URL to gs:// URI format
                if (image.startsWith('https://storage.googleapis.com/')) {
                  return image.replace('https://storage.googleapis.com/', 'gs://');
                }
                return image;
              }
              // If it's a File object, convert to base64
              return await convertImageToBase64(image);
            })
          );
          request.input_images = base64Images; // Always pass as array
        } catch (imageError) {
          console.error('Error converting images to base64:', imageError);
          // Failed to prepare request - mark as failed instead of removing
          setQueuedImages(prev => prev.map(img =>
            img.id === queuedImageId
              ? { ...img, status: 'failed' as const }
              : img
          ));
          setError('Failed to process the selected images');
          return;
        }
      }

      // Call image generation API with JSON request
      const result = await generateImage(request);

      if (result.success) {
        // API succeeded - remove the tile
        // The WebSocket will handle adding the completed image to the gallery
        setQueuedImages(prev => prev.filter(img => img.id !== queuedImageId));
      } else {
        // API failed - mark as failed and show error
        setQueuedImages(prev => prev.map(img =>
          img.id === queuedImageId
            ? { ...img, status: 'failed' as const }
            : img
        ));
        const errorMessage = result.message || 'Failed to generate image';
        setError(errorMessage);
      }

    } catch (error: any) {
      console.error('Image generation error:', error);
      // Exception - mark as failed and show error
      setQueuedImages(prev => prev.map(img =>
        img.id === queuedImageId
          ? { ...img, status: 'failed' as const }
          : img
      ));
      setError(error.message || 'Failed to generate image');
    }
  }, [
    authInitialized,
    currentUser,
    prompt,
    selectedModel,
    imageAspectRatio,
    imageResolution,
    imageGenUploadedImages,
    imageGenPreviews,
    generateSecureId,
    createOptimisticQueuedImage,
    clearGenerationForm,
    setQueuedImages
  ]);

// Handle generate with tile - creates queued video instead of modal
  const handleGenerateWithTile = useCallback(async (onAddQueuedVideo?: (queuedVideo: QueuedVideo) => void, bypassForbiddenWords: boolean = false, _currentTab: TabType = 'Videos'): Promise<void> => {
    // Clear any previous errors at the very start so each attempt is fresh
    setError(null);

    // Handle image generation for Gemini models
    if (IMAGE_MODELS.includes(selectedModel as any)) {
      return handleImageGeneration();
    }

    const validation = validateVideoGeneration(prompt, selectedModel, bypassForbiddenWords);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }

    if (!validateAuthState()) {
      return;
    }

    // Check if there's already a video generation in progress for this model family
    let modelFamily: ModelFamily;
    if (selectedModel === 'Hunyuan') {
      modelFamily = 'Hunyuan';
    } else if (selectedModel === 'Mochi') {
      modelFamily = 'Mochi';
    } else {
      modelFamily = 'VEO';
    }
    if (checkModelFamilyInProgress(modelFamily)) {
      handleModelFamilyInProgressError(modelFamily);
        return;
      }

    // Save prompt and capture current images state
    const promptText = prompt.trim();

    // Block duplicate: same prompt already queued or processing
    const isDuplicate = queuedVideos.some(
      v => (v.status === 'queued' || v.status === 'processing') && v.prompt.trim() === promptText
    );
    if (isDuplicate) {
      setError('Already in queue');
      return;
    }

    const currentImages = {
      previews: [...videoImagePreviews],
      files: [...videoUploadedImages]
    };

    // OPTIMISTIC UI: Create tile IMMEDIATELY (before API call)
    const queuedVideoId = generateSecureId('queued');
    createOptimisticQueuedVideo(queuedVideoId, promptText, currentImages, onAddQueuedVideo);

    // Clear form IMMEDIATELY after creating tile
    clearGenerationForm();

    try {
      await executeVideoGeneration(queuedVideoId, promptText, currentImages);
    } catch (err) {
      // Exception - remove the optimistic tile, restore prompt, and show error
      removeQueuedVideo(queuedVideoId);
      restoreGenerationForm(promptText, currentImages);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start video generation';
      setError(errorMessage);
    }
  }, [
    selectedModel,
    prompt,
    videoUploadedImages,
    videoImagePreviews,
    handleImageGeneration,
    validateAuthState,
    checkModelFamilyInProgress,
    handleModelFamilyInProgressError,
    generateSecureId,
    createOptimisticQueuedVideo,
    clearGenerationForm,
    executeVideoGeneration,
    removeQueuedVideo,
    restoreGenerationForm,
    queuedVideos,
  ]);

  // Helper: Try to cancel video on backend
  const tryCancelVideoOnBackend = useCallback(async (queuedVideo: QueuedVideo, queuedId: string): Promise<boolean> => {
    if (!queuedVideo.taskId) {
      return false;
    }

    if (queuedVideo.status !== 'processing' && queuedVideo.status !== 'queued') {
      return false;
    }

    try {
      const result = await cancelVideoAPI(queuedId);
      
      if (result.success) {
        return true;
      }
    } catch (cancelError) {
      console.error('Failed to cancel video generation:', cancelError);
    }

    return false;
  }, [cancelVideoAPI]);

  // Helper: Remove item from queued lists
  const removeFromQueuedLists = useCallback((queuedVideo: QueuedVideo | undefined, queuedImage: QueuedVideo | undefined, queuedId: string) => {
    if (queuedVideo) {
      setQueuedVideos(prev => prev.filter(v => v.id !== queuedId));
    }

    if (queuedImage) {
      setQueuedImages(prev => prev.filter(img => img.id !== queuedId));
    }
  }, [setQueuedVideos, setQueuedImages]);

  // cancel queue video or image
  const cancelQueuedVideo = useCallback(async (queuedId: string): Promise<void> => {
    const queuedVideo = queuedVideos.find(v => v.id === queuedId);
    const queuedImage = queuedImages.find(img => img.id === queuedId);
    
    if (!queuedVideo && !queuedImage) {
      return;
    }

    try {
      // Try to cancel video on backend if applicable
      if (queuedVideo) {
        const cancelled = await tryCancelVideoOnBackend(queuedVideo, queuedId);
        if (cancelled) {
          // Immediately remove from UI (optimistic update)
          setQueuedVideos(prev => prev.filter(v => v.id !== queuedId));
          return;
        }
      }

      // Remove from the appropriate array
      removeFromQueuedLists(queuedVideo, queuedImage, queuedId);

    } catch (error) {
      console.error('Error during queue cancellation:', error);
      
      // Still remove from list even if cancellation failed
      removeFromQueuedLists(queuedVideo, queuedImage, queuedId);
    }
  }, [queuedVideos, queuedImages, tryCancelVideoOnBackend, removeFromQueuedLists, setQueuedVideos]);

  // cancel/remove queued image
  const cancelQueuedImage = useCallback(async (queuedId: string): Promise<void> => {
    const queuedImage = queuedImages.find(img => img.id === queuedId);
    if (!queuedImage) {
      return;
    }

    try {
      // Remove from queued images list - this will also update localStorage
      setQueuedImages(prev => prev.filter(img => img.id !== queuedId));

    } catch (error) {
      console.error('Error removing queued image:', error);
      // Still remove from list even if there's an error
      setQueuedImages(prev => prev.filter(img => img.id !== queuedId));
    }
  }, [queuedImages, setQueuedImages]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (saveTimers.current.videos) {
        clearTimeout(saveTimers.current.videos);
      }
      if (saveTimers.current.images) {
        clearTimeout(saveTimers.current.images);
      }
      if (saveTimers.current.uploading) {
        clearTimeout(saveTimers.current.uploading);
      }
    };
  }, []);

  return {
    prompt,
    selectedModel,
    isVideoGenerating,
    setIsVideoGenerating,
    isGenerating,
    error,
    progress,
    progressMessage,
    generatedVideo,
    selectedImage,
    imagePreview,
    generateAudio,
    aspectRatio,
    imageAspectRatio,
    imageResolution,
    isUploading,
    uploadError,
    uploadSuccess,
    canCancel,
    isCancelling,
    wasCancelled: wasCancelledRef.current,
    queuedVideos,
    queuedImages,
    uploadingVideos,
    uploadedImagesCount: videoUploadedImages.length + imageGenUploadedImages.length,
    setPrompt,
    setAspectRatio,
    setImageAspectRatio,
    setImageResolution,
    handleModelSelect,
    handleGenerateWithTile,
    handleCancel,
    cancelQueuedVideo,
    cancelQueuedImage,
    clearError,
    handleImageUpload,
    removeImage,
    removeImageAtIndex,
    videoUploadedImages,
    videoImagePreviews,
    setVideoUploadedImages,
    setVideoImagePreviews,
    imageGenUploadedImages,
    imageGenPreviews,
    setImageGenUploadedImages,
    setImageGenPreviews,
    toggleAudio,
    handleVideoUpload,
    clearUploadError,
    clearUploadSuccess,
    handleVideoGenerated,
    getMaxImageCount,
    originalVideoId,
    setOriginalVideoId
  };
};
